// Group encryption (Signal style): everything is encrypted on the phone with the group's AES-GCM key, which only
// travels inside the invite link (the part after #, which browsers never send to a server).
import { b64uDecode, b64uEncode } from "../../shared/b64u.ts";
import type { Group, Payload, RelayMessage, Sender } from "../../shared/model.ts";

const keyCache = new Map<string, Promise<CryptoKey>>();
function cryptoKey(g: Group): Promise<CryptoKey> {
  let k = keyCache.get(g.id);
  if (!k) { k = crypto.subtle.importKey("raw", b64uDecode(g.key), "AES-GCM", false, ["encrypt", "decrypt"]); keyCache.set(g.id, k); }
  return k;
}
export const forgetKey = (groupId: string): void => { keyCache.delete(groupId); };

/** A new random group key. */
export const newKey = (): string => b64uEncode(crypto.getRandomValues(new Uint8Array(32)));

const sha256 = async (text: string) => new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)));
/** The relay checks a token made from the key, so only people with the invite link can read or post. */
export const tokenFor = async (key: string): Promise<string> => b64uEncode(await sha256("hfl-relay-token:" + key));
/** The group id is made from the token, so the relay can check access without storing anything (server/relay.ts). */
export const groupIdFor = async (token: string): Promise<string> => b64uEncode(await sha256("hfl-group-id:" + token)).slice(0, 22);

export type Plain = Payload & Sender;

export async function encrypt(g: Group, msg: Plain): Promise<Pick<RelayMessage, "ct" | "iv">> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, await cryptoKey(g), new TextEncoder().encode(JSON.stringify(msg)));
  return { ct: b64uEncode(ct), iv: b64uEncode(iv) };
}
/** Throws when the message wasn't made with this group's key. */
export async function decrypt(g: Group, m: Pick<RelayMessage, "ct" | "iv">): Promise<Plain> {
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: b64uDecode(m.iv) }, await cryptoKey(g), b64uDecode(m.ct));
  return JSON.parse(new TextDecoder().decode(pt)) as Plain;
}
